/**
 * MainTreemapTile — quick-260626-cot Task 3.1.
 *
 * Analytical replacement for the Deals over $200k table (MAIN-11). Each open
 * deal is a treemap node sized by Amount, colored by stage, labeled with the
 * account name (top-left) plus amount + stage (bottom-right). Clicking a node
 * opens the deal in Salesforce.
 *
 * Data shape (reused verbatim from the MAIN-11 grouped payload — see
 * discovery-deals-200k.ts): each bucket is
 *   { oppId, name, amount, stage, closeDate, accountId, accountName, ownerName }.
 *
 * UX-07 4-state machine: loading / cache-warming / sf-unreachable / success —
 * mirrors MainTableTile so the family stays consistent.
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import { formatAmount } from '../../lib/format.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';

import type { MainGroupedResponse } from '@saledash/shared';
import type { EChartsOption } from 'echarts';

interface MainTreemapTileProps {
  metricId: string;
  title: string;
  tooltipFormula?: string | undefined;
  data: MainGroupedResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** A single deal node parsed off the MAIN-11 bucket array. */
interface DealNode {
  oppId: string;
  name: string;
  amount: number;
  stage: string;
  accountName: string;
  ownerName: string;
  /** ISO close date — quick-260630-f7f Task 7 item 11: shown inline on the node. */
  closeDate: string;
}

const MONTH_ABBR_TREEMAP = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format an ISO close date as a compact "Aug '26" month label (quick-260630-f7f
 * Task 7 item 11). Returns '' for missing/invalid dates so the node omits it.
 */
function closeMonthLabel(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mon = MONTH_ABBR_TREEMAP[d.getUTCMonth()] ?? '';
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${mon} '${yy}`;
}

/**
 * Stage → color. Pipeline stages run cool-navy (early) to bright-cyan (late) so
 * the treemap reads "how far along" at a glance, matching the mock's stage
 * palette (Scoping/Discovery navy → Demo bright cyan). Unknown stages fall back
 * to a mid ink so they still render.
 */
const STAGE_COLORS: Record<string, string> = {
  Discovery: '#0B2A4A',
  Scoping: '#0B2A4A',
  Qualification: '#13395c',
  Demo: '#4FC3DD',
  Proposal: '#163E63',
  Negotiation: '#1AA7C8',
  'Verbal Commit': '#1f7fa0',
};
const STAGE_FALLBACK = '#4E5A6B';

function stageColor(stage: string): string {
  return STAGE_COLORS[stage] ?? STAGE_FALLBACK;
}

/** Parse the rich MAIN-11 buckets into typed deal nodes (drop malformed rows). */
function toDealNodes(buckets: MainGroupedResponse['buckets']): DealNode[] {
  const nodes: DealNode[] = [];
  for (const b of buckets) {
    const oppId = typeof b.oppId === 'string' ? b.oppId : '';
    const amount = typeof b.amount === 'number' ? b.amount : 0;
    if (!oppId || amount <= 0) continue;
    nodes.push({
      oppId,
      name: typeof b.name === 'string' ? b.name : '',
      amount,
      stage: typeof b.stage === 'string' ? b.stage : '',
      accountName: typeof b.accountName === 'string' ? b.accountName : '',
      ownerName: typeof b.ownerName === 'string' ? b.ownerName : '',
      closeDate: typeof b.closeDate === 'string' ? b.closeDate : '',
    });
  }
  return nodes;
}

/**
 * Build the absolute /api/sf/link/:id href. Mirrors MainTableTile's bridge: on
 * the production host the api lives on api.salesdashboard.adeptia.org (the SPA
 * is cross-origin on cro.*); locally it's a same-origin relative path.
 */
function sfLinkHref(oppId: string): string {
  const apiBase =
    typeof window !== 'undefined' &&
    window.location.hostname.endsWith('salesdashboard.adeptia.org')
      ? `${window.location.protocol}//api.salesdashboard.adeptia.org`
      : '';
  return `${apiBase}/api/sf/link/${oppId}`;
}

/** Distinct stages present, in first-seen order, for the HTML legend. */
function distinctStages(nodes: DealNode[]): string[] {
  const seen: string[] = [];
  for (const n of nodes) {
    if (n.stage && !seen.includes(n.stage)) seen.push(n.stage);
  }
  return seen;
}

function buildTreemapOption(nodes: DealNode[]): EChartsOption {
  return {
    animation: false,
    tooltip: {
      backgroundColor: 'rgba(17,24,39,0.92)',
      borderColor: 'transparent',
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: (params: unknown) => {
        const p = params as { data?: { deal?: DealNode } };
        const d = p.data?.deal;
        if (!d) return '';
        const owner = d.ownerName ? `<br/>${d.ownerName}` : '';
        return `<b>${d.accountName || d.name}</b><br/>${formatAmount(
          d.amount,
        )} · ${d.stage}${owner}`;
      },
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 3,
          gapWidth: 3,
          borderRadius: 8,
        },
        label: {
          show: true,
          position: 'insideTopLeft',
          formatter: (params: unknown) => {
            const p = params as { data?: { deal?: DealNode } };
            const d = p.data?.deal;
            if (!d) return '';
            // account top line, then amount + stage, then AE name + close month
            // INLINE (quick-260630-f7f Task 7 item 11 — not just on hover).
            // ECharts stacks rich lines; small nodes truncate gracefully.
            const aeMonthParts: string[] = [];
            if (d.ownerName) aeMonthParts.push(d.ownerName);
            const cm = closeMonthLabel(d.closeDate);
            if (cm) aeMonthParts.push(cm);
            const aeMonthLine =
              aeMonthParts.length > 0 ? `\n{ae|${aeMonthParts.join(' · ')}}` : '';
            return `{acct|${d.accountName || d.name}}\n{amt|${formatAmount(
              d.amount,
            )}}  {stage|${d.stage}}${aeMonthLine}`;
          },
          rich: {
            acct: {
              fontSize: 13,
              fontWeight: 'bold',
              color: '#fff',
              lineHeight: 18,
            },
            amt: {
              fontSize: 15,
              fontWeight: 'bold',
              color: 'rgba(255,255,255,0.95)',
              lineHeight: 22,
            },
            stage: {
              fontSize: 11,
              color: 'rgba(255,255,255,0.85)',
            },
            // AE name + expected close month, shown beneath amount/stage.
            ae: {
              fontSize: 11,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 16,
            },
          },
        },
        data: nodes.map((d) => ({
          name: d.accountName || d.name,
          value: d.amount,
          deal: d,
          itemStyle: { color: stageColor(d.stage) },
        })),
      },
    ],
  };
}

export function MainTreemapTile({
  metricId,
  title,
  tooltipFormula,
  data,
  isLoading,
  isError,
  error,
  refetch,
}: MainTreemapTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {title}
      </CardTitle>
      {tooltipFormula && (
        <Tooltip trigger="?" label="Show definition">
          {tooltipContent}
        </Tooltip>
      )}
    </CardHeader>
  );

  // UX-07: cache-warming
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <SfUnreachableBanner onRetry={refetch} />
        </div>
      </Card>
    );
  }

  // Loading
  if (isLoading || !data) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4" aria-busy="true">
          <Skeleton className="h-60 w-full" data-testid="skeleton-treemap" />
        </div>
      </Card>
    );
  }

  const nodes = toDealNodes(data.buckets);
  const totalValue =
    data.total_value ?? nodes.reduce((sum, n) => sum + n.amount, 0);
  const stages = distinctStages(nodes);

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      {header}

      {/* Context line — total open $ across the deals shown. */}
      <p className="mb-2 text-xs text-ink-500">
        {nodes.length > 0 ? (
          <>
            <span className="font-semibold tabular-nums text-ink-900">
              {formatAmount(totalValue)}
            </span>{' '}
            open across {nodes.length} deal{nodes.length === 1 ? '' : 's'} ·
            sized by amount · click → Salesforce
          </>
        ) : (
          'sized by amount · click → Salesforce'
        )}
      </p>

      {/* Empty state */}
      {nodes.length === 0 && (
        <p
          className="py-12 text-center text-sm text-ink-500"
          data-testid="treemap-empty-state"
        >
          No deals match the current filter.
        </p>
      )}

      {/* Treemap */}
      {nodes.length > 0 && (
        <>
          <div
            className="h-60 w-full"
            data-testid="treemap-container"
          >
            <ReactECharts
              option={buildTreemapOption(nodes)}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              notMerge
              lazyUpdate={false}
              onEvents={{
                click: (params: unknown) => {
                  const p = params as { data?: { deal?: DealNode } };
                  const oppId = p.data?.deal?.oppId;
                  if (oppId && typeof window !== 'undefined') {
                    window.open(
                      sfLinkHref(oppId),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                },
              }}
            />
          </div>

          {/* Stage legend — color square + stage name. */}
          <div
            className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500"
            data-testid="treemap-legend"
          >
            {stages.map((stage) => (
              <span key={stage} className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: stageColor(stage) }}
                />
                {stage}
              </span>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
